import { Modal, Accordion, AccordionItem } from '@carbon/react';
import { CheckmarkFilled, CloseFilled } from '@carbon/react/icons';
import classNames from 'classnames';
import { formatNumber } from 'utils/number';
import style from './methodology-modal.module.scss';
import { ReconcilerDefaultsResponse } from './types';

type MethodologyModalProps = {
  open: boolean;
  onClose: () => void;
  defaults: ReconcilerDefaultsResponse;
};

export const MethodologyModal = ({
  open,
  onClose,
  defaults,
}: MethodologyModalProps) => {
  // Convert from decimal to percentage for display (e.g., 0.01 -> 1%)
  const overlapThresholdPercent = defaults.shapeInclusionThreshold * 100;
  const shapeMatchThresholdPercent = defaults.shapeSimilarityThreshold * 100;
  return (
    <Modal
      open={open}
      onRequestClose={onClose}
      modalHeading="How Reconciliation Works"
      passiveModal
      size="md"
    >
      <div className={style.methodologyModalContent}>
        <p className={style.overview}>
          This tool compares ownership calculations between the new{' '}
          <strong>Title Validator</strong> and{' '}
          <strong>Entity Title Assets</strong> (generated by the Java title
          workspace calculator) to validate accuracy.
        </p>

        <Accordion>
          <AccordionItem title="What gets compared">
            <div className={style.accordionContent}>
              <div className={style.comparisonItem}>
                <strong>Title Validator Output Shapes:</strong>
                <p>
                  Ownership geometries extracted from document title chains by
                  the new Title Validator calculation engine, organized by
                  geometry name.
                </p>
              </div>
              <div className={style.comparisonItem}>
                <strong>Entity Title Assets:</strong>
                <p>
                  Existing production system calculated by the Java title
                  workspace calculator. Results are grouped by shape{' '}
                  <em>and</em> title workspace.
                </p>
              </div>
              <div className={style.callout}>
                <strong>Note:</strong> The same shape can appear in multiple
                workspaces with different interest values, which is why
                workspace IDs are displayed separately.
              </div>

              <div
                className={classNames(
                  style.comparisonItem,
                  style.comparisonItemSpaced
                )}
              >
                <strong>Which entity title asset shapes are shown?</strong>
                <p>
                  An entity may own interests across many locations, but when
                  reconciling a specific document, we only care about shapes
                  relevant to where that document was validated.
                </p>
              </div>

              <div className={style.filteringDiagram}>
                <div className={style.filteringColumn}>
                  <div className={style.filteringColumnHeader}>
                    Validated Location
                  </div>
                  <div className={style.filteringColumnSubheader}>
                    (from document)
                  </div>
                  <div className={style.validatedLocationBox}>
                    <div className={style.locationLabel}>TX-Borden</div>
                    <div className={style.locationLabel}>Section 42</div>
                  </div>
                </div>

                <div className={style.filteringArrow}>
                  <div className={style.arrowLine} />
                  <div className={style.arrowLabel}>overlap?</div>
                  <div className={style.arrowHead} />
                </div>

                <div className={style.filteringColumn}>
                  <div className={style.filteringColumnHeader}>
                    Entity&apos;s Shapes
                  </div>
                  <div className={style.filteringColumnSubheader}>
                    (50 total across Texas)
                  </div>
                  <div className={style.shapesGrid}>
                    <div className={style.shapeCircle} />
                    <div className={style.shapeCircle} />
                    <div className={style.shapeCircle} />
                    <div className={style.shapeCircle} />
                    <div className={style.shapeCircle} />
                    <div className={style.shapeCircle} />
                    <div className={style.shapeSquare} />
                    <div className={style.shapeSquare} />
                    <div className={style.shapeSquare} />
                    <div className={style.shapeCircle} />
                    <div className={style.shapeCircle} />
                    <div className={style.shapeCircle} />
                    <div className={style.shapeCircle} />
                    <div className={style.shapeCircle} />
                    <div className={style.shapeCircle} />
                  </div>
                  <div className={style.shapesLegend}>
                    <div className={style.legendItem}>
                      <div className={style.legendSquare} />
                      <span>3 shapes overlap</span>
                    </div>
                    <div className={style.legendItem}>
                      <div className={style.legendCircle} />
                      <span>47 shapes don&apos;t overlap</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className={style.filteringToggleComparison}>
                <div className={style.toggleOption}>
                  <div className={style.toggleCheckbox}>
                    <span className={style.uncheckedBox} />
                  </div>
                  <div className={style.toggleDetails}>
                    <div className={style.toggleLabel}>Unchecked (default)</div>
                    <div className={style.toggleDescription}>
                      Shows <strong>3 shapes</strong>
                    </div>
                    <div className={style.toggleSubtext}>
                      Only those overlapping the validated location
                    </div>
                  </div>
                </div>

                <div className={style.toggleOption}>
                  <div className={style.toggleCheckbox}>
                    <span className={style.checkedBox} />
                  </div>
                  <div className={style.toggleDetails}>
                    <div className={style.toggleLabel}>Checked (show all)</div>
                    <div className={style.toggleDescription}>
                      Shows <strong>50 shapes</strong>
                    </div>
                    <div className={style.toggleSubtext}>
                      Entire entity portfolio across all locations
                    </div>
                  </div>
                </div>
              </div>

              <div className={style.callout}>
                <strong>Why filter by default?</strong> Entities can have
                hundreds of shapes across many locations. Filtering to only
                those overlapping the validated location helps you focus on
                what&apos;s relevant to the document being reconciled.
              </div>
            </div>
          </AccordionItem>

          <AccordionItem title="Configurable parameters">
            <div className={style.accordionContent}>
              <p>
                You can configure the following parameters to control how
                reconciliation works:
              </p>
              <ul>
                <li>
                  <strong>Document:</strong> The root document from which the
                  Title Validator begins tracing the chain of title. This
                  determines which transactions are analyzed when calculating
                  ownership interests.
                </li>
                <li>
                  <strong>Target Entity:</strong> The entity whose ownership
                  interests are being validated. The reconciler compares this
                  entity&apos;s interests between Title Validator and Entity
                  Title Assets.
                </li>
                <li>
                  <strong>Shape Inclusion Threshold (%):</strong> Controls which
                  Entity Title Asset shapes are included in the comparison. Only
                  shapes where at least this percentage of their total area
                  overlaps with a Title Validator output shape will be
                  considered. Higher values (e.g., 50%) require more substantial
                  overlap, while lower values (e.g., 1%) include shapes with
                  minimal overlap.
                  <div className={style.callout}>
                    <strong>Default:</strong>{' '}
                    {formatNumber(
                      overlapThresholdPercent,
                      2,
                      2,
                      'decimal',
                      false
                    )}
                    % - includes shapes with at least{' '}
                    {formatNumber(
                      overlapThresholdPercent,
                      2,
                      2,
                      'decimal',
                      false
                    )}
                    % overlap
                  </div>
                </li>
                <li>
                  <strong>Shape Similarity Threshold (%):</strong> Determines
                  whether shapes are considered a &quot;match&quot; based on how
                  similar they are. This uses Intersection over Union (IoU) to
                  measure similarity: 100% means identical shapes, while lower
                  values indicate differences in size or position. If the
                  similarity meets or exceeds this threshold, the shapes are
                  marked as matching.
                  <div className={style.callout}>
                    <strong>Default:</strong>{' '}
                    {formatNumber(
                      shapeMatchThresholdPercent,
                      0,
                      0,
                      'decimal',
                      false
                    )}
                    % - shapes with at least{' '}
                    {formatNumber(
                      shapeMatchThresholdPercent,
                      0,
                      0,
                      'decimal',
                      false
                    )}
                    % similarity are considered matching
                  </div>
                </li>
                <li>
                  <strong>Interest Difference Threshold:</strong> Controls how
                  precisely interest values must match to be considered
                  identical. This accounts for floating-point arithmetic
                  precision differences. Lower values require more exact
                  matches, while higher values are more lenient.
                  <div className={style.callout}>
                    <strong>Default:</strong>{' '}
                    {defaults.interestDifferenceThreshold} (
                    {defaults.interestDifferenceThreshold.toExponential(0)}) -
                    allows for minor floating-point differences
                  </div>
                </li>
                <li>
                  <strong>Show all entity shapes:</strong> Controls whether to
                  display all Entity Title Asset shapes for the entity, or only
                  those that spatially overlap with the validated locations.
                  <div className={style.callout}>
                    <strong>Default:</strong> Unchecked - only shapes
                    overlapping the validated locations are shown, helping you
                    focus on what&apos;s relevant to the document being
                    reconciled.
                  </div>
                </li>
              </ul>
              <p className={style.helperText}>
                Adjust these parameters if you need stricter or more lenient
                comparison criteria for your specific use case.
              </p>
            </div>
          </AccordionItem>

          <AccordionItem title="How comparison works">
            <div className={style.accordionContent}>
              <ul>
                <li>
                  <strong>Finds overlapping shapes:</strong> Identifies Entity
                  Title Asset shapes that overlap with Title Validator output
                  shapes using the <strong>Shape Inclusion Threshold</strong>{' '}
                  (default{' '}
                  {formatNumber(
                    overlapThresholdPercent,
                    2,
                    2,
                    'decimal',
                    false
                  )}
                  %). Only shapes where at least this percentage of their total
                  area intersects with a Title Validator output shape are
                  included in the comparison.
                </li>
                <li>
                  <strong>Smart grouping:</strong> When multiple Entity Title
                  Asset shapes overlap a Title Validator output shape, only
                  combines shapes that ALL overlap with each other. For example,
                  if shapes A and B overlap, and shapes A and C overlap, but B
                  and C do not overlap—the system tests A+B separately from C.
                  This ensures we only sum interests that actually cover the
                  same physical area.
                </li>
                <li>
                  <strong>Workspace constraint:</strong> Only combines shapes
                  from the same title workspace, even if they spatially overlap.
                </li>
                <li>
                  <strong>Value comparison:</strong> After identifying
                  overlapping shapes, the system compares interest values
                  (working, royalty, etc.) between Entity Title Assets and Title
                  Validator. Values are considered matched if they differ by
                  less than the configurable{' '}
                  <strong>Interest Difference Threshold</strong>. This accounts
                  for minor floating-point precision differences while catching
                  meaningful discrepancies.
                </li>
              </ul>
            </div>
          </AccordionItem>

          <AccordionItem title="Understanding results">
            <div className={style.accordionContent}>
              <p>
                <strong>Summary Statistics:</strong>
              </p>
              <p className={style.helperText}>
                The summary bar shows key metrics about the reconciliation:
              </p>
              <ul className={style.statsList}>
                <li>
                  <strong>Title Validator Input Locations:</strong> Number of
                  input locations from the document
                </li>
                <li>
                  <strong>Title Validator Output Shapes:</strong> Number of
                  ownership shapes generated by the Title Validator
                </li>
                <li>
                  <strong>Entity Title Asset Shapes:</strong> Number of existing
                  shapes being compared
                </li>
                <li>
                  <strong>Total Comparisons:</strong> Number of shape-pair
                  comparisons (one per Title Validator output shape vs Entity
                  Title Asset shape combination)
                </li>
                <li>
                  <strong>Matched Comparisons:</strong> Shape similarity meets
                  threshold AND all interest values match within tolerance
                </li>
                <li>
                  <strong>Mismatched Comparisons:</strong> Shape similarity
                  below threshold OR not all interest values match. Expand the
                  row to see which specific values differ.
                </li>
              </ul>

              <p>
                <strong>Inline Verification Icons:</strong>
              </p>
              <p className={style.helperText}>
                Each value in the Interest Difference and Shape Match columns
                shows a verification icon indicating whether it matches:
              </p>
              <div className={style.statusIndicators}>
                <div className={style.statusItem}>
                  <span className={style.verificationIcon}>
                    <CheckmarkFilled className={style.validIcon} />
                    <span>match</span>
                  </span>
                  <span className={style.statusDescription}>
                    Value is within tolerance / shape similarity meets threshold
                  </span>
                </div>
                <div className={style.statusItem}>
                  <span className={style.verificationIcon}>
                    <CloseFilled className={style.invalidIcon} />
                    <span>mismatch</span>
                  </span>
                  <span className={style.statusDescription}>
                    Value exceeds tolerance / shape similarity below threshold
                  </span>
                </div>
              </div>

              <p>
                <strong>Reading the table:</strong>
              </p>
              <ul>
                <li>
                  <strong>Multiple geometries:</strong> An entity can have
                  ownership in multiple locations (e.g., TX-Borden T4N-42,
                  T4N-41). Each geometry is compared separately.
                </li>
                <li>
                  <strong>Multiple shapes per output shape:</strong> When
                  several Entity Title Asset shapes overlap a Title Validator
                  output shape, each combination is tested. The summary shows
                  total comparisons and match/mismatch counts.
                </li>
                <li>
                  <strong>Workspace IDs:</strong> The same physical shape may
                  appear multiple times with different workspace IDs if it is
                  calculated in different title workspaces with different
                  results.
                </li>
                <li>
                  <strong>
                    Interest Difference &amp; Shape Match columns:
                  </strong>{' '}
                  Each value shows a verification icon indicating whether it
                  passes. Values are color-coded:{' '}
                  <span className={style.colorSuccess}>green</span> for passing,{' '}
                  <span className={style.colorError}>red</span> for failing.
                </li>
                <li>
                  <strong>Expandable rows:</strong> Click the chevron to see
                  detailed interest-by-interest comparisons (working,
                  overriding, royalty, etc.).
                </li>
              </ul>

              <p>
                <strong>Map Legend Categories:</strong>
              </p>
              <p className={style.helperText}>
                The summary map groups comparisons into two categories:
              </p>
              <div className={style.statusIndicators}>
                <div className={style.statusItem}>
                  <span className={style.mapLegendItem}>
                    <span className={style.mapLegendColorMatched} />
                    <span>Matched Comparisons</span>
                  </span>
                  <span className={style.statusDescription}>
                    Both interest values AND shape similarity match
                  </span>
                </div>
                <div className={style.statusItem}>
                  <span className={style.mapLegendItem}>
                    <span className={style.mapLegendColorMismatched} />
                    <span>Mismatched Comparisons</span>
                  </span>
                  <span className={style.statusDescription}>
                    Either shape or interests (or both) don&apos;t match
                  </span>
                </div>
              </div>

              <p className={style.helperText}>
                Use &quot;Show Map&quot; to visualize the spatial overlap
                between Title Validator output shapes and Entity Title Asset
                shapes.
              </p>
            </div>
          </AccordionItem>
        </Accordion>
      </div>
    </Modal>
  );
};
